import React, { useEffect, useRef } from 'react';

const sanitizeHtml = (html) => {
  const temp = document.createElement('div');
  temp.innerHTML = html;

  const scripts = temp.querySelectorAll('script');
  scripts.forEach((script) => script.remove());

  const allElements = temp.querySelectorAll('*');
  allElements.forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  return temp.innerHTML;
};

const NotesEditorModal = ({
  isOpen,
  title = 'Edit Notes Content',
  initialContent = '',
  isSaving = false,
  onClose,
  onSave,
  presentation = 'modal'
}) => {
  const editableRef = useRef(null);

  useEffect(() => {
    if (isOpen && editableRef.current) {
      editableRef.current.innerHTML = initialContent || '';
    }
  }, [initialContent, isOpen]);

  if (!isOpen) return null;

  const applyFormat = (command) => {
    document.execCommand(command);
  };

  const handleSave = () => {
    if (!editableRef.current || typeof onSave !== 'function') return;

    const sanitizedContent = sanitizeHtml(editableRef.current.innerHTML);
    onSave(sanitizedContent);
  };

  const editorContent = (
    <>
      <div className="mb-3 flex items-center justify-between">
        {presentation === 'inline' ? (
          <label className="block text-sm font-medium text-gray-700">{title}</label>
        ) : <span />}
        <div className="flex items-center space-x-2">
          <button onClick={() => applyFormat('bold')} className="px-2 py-1 border rounded text-sm hover:bg-gray-100" title="Bold">
            <strong>B</strong>
          </button>
          <button onClick={() => applyFormat('italic')} className="px-2 py-1 border rounded text-sm hover:bg-gray-100" title="Italic">
            <em>I</em>
          </button>
          <button onClick={() => applyFormat('underline')} className="px-2 py-1 border rounded text-sm hover:bg-gray-100" title="Underline">
            <u>U</u>
          </button>
          <button onClick={() => applyFormat('insertUnorderedList')} className="px-2 py-1 border rounded text-sm hover:bg-gray-100" title="Bullet List">
            • List
          </button>
          <button onClick={() => applyFormat('insertOrderedList')} className="px-2 py-1 border rounded text-sm hover:bg-gray-100" title="Numbered List">
            1. List
          </button>
        </div>
      </div>

      <div
        ref={editableRef}
        contentEditable
        suppressContentEditableWarning
        className="prose max-w-none w-full min-h-96 max-h-96 overflow-auto px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
        style={{ lineHeight: '1.6', fontFamily: 'inherit' }}
      />

      <div className="mt-3 flex items-center justify-end gap-2">
        <button
          onClick={onClose}
          className="py-2 px-4 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="py-2 px-4 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </>
  );

  if (presentation === 'inline') {
    return (
      <div className="border rounded-lg p-4 bg-white">
        {editorContent}
        <p className="text-xs text-gray-500 mt-2">
          ℹ️ You can edit the text directly. Use the toolbar buttons for basic formatting. Math equations and complex formatting will be preserved.
        </p>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 sm:p-6 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl sm:text-3xl font-bold w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center rounded-full hover:bg-gray-200 transition-all" aria-label="Close edit notes dialog"><svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} aria-hidden="true"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg></button>
        </div>
        <div className="p-4 sm:p-6 overflow-y-auto flex-1">{editorContent}</div>
      </div>
    </div>
  );
};

export default NotesEditorModal;